import React, { useState } from "react";
import { Maximize2, Minimize2, FileDown, Loader } from "lucide-react";
import { downloadResultPdf } from "../lib/resultPdf";

/**
 * Tiny toolbar that emits a `resmo:result-section:set-all` CustomEvent so all
 * ResultSection instances matching the given `group` expand or collapse at
 * once. Also exposes a "Save as PDF" button that rasterises the sections
 * wrapper into a multi-page A4 PDF (via /app/frontend/src/lib/resultPdf.js).
 *
 * Lives at the top of result pages (Skills, RoleResearch, Dashboard,
 * InterviewQuestions).
 *
 * Props:
 *   - group           Required. Group key all ResultSections share.
 *   - targetSelector  Required for PDF. CSS selector for the sections wrapper.
 *   - pdfTitle        Required for PDF. Header text printed at the top of every page.
 *   - pdfFilename     Optional. Default: "resmo-report.pdf".
 *   - testid          Optional override for the toolbar's outer data-testid.
 */
export default function SectionExpandToolbar({
  group,
  targetSelector,
  pdfTitle,
  pdfFilename = "resmo-report.pdf",
  testid,
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const fire = (open) => {
    window.dispatchEvent(
      new CustomEvent("resmo:result-section:set-all", { detail: { group, open } }),
    );
  };

  const handleDownload = async () => {
    if (!targetSelector) return;
    setError("");
    setDownloading(true);
    try {
      await downloadResultPdf({
        group,
        targetSelector,
        filename: pdfFilename,
        headerTitle: pdfTitle || "Resmo report",
      });
    } catch (e) {
      setError(e?.message || "Couldn't build PDF");
    } finally {
      setDownloading(false);
    }
  };

  const ti = testid || `${group}-toolbar`;
  const pillStyle = {
    background: "white",
    color: "#0F1B2D",
    border: "2px solid #0F1B2D",
    borderRadius: 6,
    fontWeight: 700,
  };

  return (
    <div className="flex items-center justify-end gap-2 mb-2 flex-wrap" data-testid={ti}>
      <button
        type="button"
        onClick={() => fire(true)}
        className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1"
        style={pillStyle}
        data-testid={`${ti}-expand-all`}
        title="Expand every section"
      >
        <Maximize2 size={11} /> Expand all
      </button>
      <button
        type="button"
        onClick={() => fire(false)}
        className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1"
        style={pillStyle}
        data-testid={`${ti}-collapse-all`}
        title="Collapse every section"
      >
        <Minimize2 size={11} /> Collapse all
      </button>
      {targetSelector && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1"
          style={{
            ...pillStyle,
            background: downloading ? "#FFFBEB" : "#0F1B2D",
            color: downloading ? "#0F1B2D" : "#FFC145",
            borderColor: "#0F1B2D",
          }}
          data-testid={`${ti}-save-pdf`}
          title="Download a printable PDF of this report"
        >
          {downloading ? <Loader size={11} className="animate-spin" /> : <FileDown size={11} />}
          {downloading ? "Building…" : "Save as PDF"}
        </button>
      )}
      {error && (
        <span
          className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1"
          style={{
            background: "#FEF2F2",
            color: "#991B1B",
            border: "2px solid #EF4444",
            borderRadius: 6,
            fontWeight: 700,
          }}
          data-testid={`${ti}-pdf-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
}
